<template>
  <v-navigation-drawer
    class="h-100 d-flex flex-column justify-center"
    fixed
    overlay-opacity="0.7"
    right
    temporary
    width="450"
    :value="visible"
    @input="handleInput"
  >
    <v-progress-linear
      indeterminate
      height="7"
      :active="isLoading"
    ></v-progress-linear>

    <div class="my-6 px-6">
      <span v-if="!isLoading" class="h6">
        {{ $t('ai_features.chat_view.my_past_conversations') }}
      </span>
    </div>

    <div
      v-if="showEmptyState"
      class="d-flex justify-center flex-column align-center empty-state-container"
    >
      <v-icon color="blue-grey lighten-2" large>
        mdi-beehive-outline
      </v-icon>

      <span class="subtitle-2 blue-grey--text mt-4">
        {{ $t('ai_features.chat_view.no_past_conversations') }}</span
      >
    </div>

    <v-list v-if="!isLoading && !showEmptyState" nav dense expand class="px-6">
      <v-list-group
        v-for="pastConversation in pastConversations"
        :key="pastConversation.month"
        v-model="pastConversation.active"
        activeClass="past-conversation-active-class"
        no-action
      >
        <template v-slot:activator>
          <v-list-item-content>
            <v-list-item-title>
              <span class="body-3 my-2">{{ pastConversation.month }}</span>
            </v-list-item-title>
          </v-list-item-content>
        </template>
        <v-list-item
          v-for="conversation in pastConversation.conversations"
          :key="conversation.id"
          class="pl-12 cursor-pointer d-flex align-start"
          :class="{
            ' selected-conversation':
              conversation.id === selectedConversationId,
          }"
          @click="$emit('selectConversation', conversation)"
        >
          <v-list-item-content>
            <v-list-item-title>
              <span class="caption conversation-title" v-snip="1">
                {{ conversation.title }}
              </span>
            </v-list-item-title>
            <v-list-item-subtitle>
              <span class="caption">
                {{ conversation.dateText }}
              </span>
            </v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action class="ma-0 pa-0 d-flex align-center">
            <v-icon
              class="conversation-delete-icon"
              color="blue-grey"
              @click.stop="$emit('deleteConversation', conversation)"
            >
              mdi-trash-can-outline
            </v-icon>
          </v-list-item-action>
        </v-list-item>
      </v-list-group>
    </v-list>
  </v-navigation-drawer>
</template>

<script>
import { mapGetters } from 'vuex'

import mixin from '@/mixins/mixin.vue'
import api from '@/api'

const DEFAULT_LANGUAGE = 'en-US'

const LIMIT_PER_PAGE = 50

export default {
  name: 'PastConversationsDrawer',

  mixins: [mixin],

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    selectedConversationId: {
      type: String,
      default: null,
    },
  },

  mounted() {
    this.getPastConversations()
  },

  data() {
    return {
      isLoading: false,
      pastConversations: {},
      numberOfConversations: 0,
      LIMIT_PER_PAGE,
    }
  },

  computed: {
    ...mapGetters(['organizationId']),

    userLanguage() {
      return window.navigator.language || DEFAULT_LANGUAGE
    },

    showEmptyState() {
      return !this.isLoading && this.numberOfConversations === 0
    },
  },

  methods: {
    async getPastConversations() {
      try {
        this.isLoading = true
        const pastConversations = await api.ai.getChats(this.organizationId, {
          limit: LIMIT_PER_PAGE,
        })
        this.numberOfConversations = pastConversations.data.totalRecordsInData
        const organizedConversations = this.organizeConversationsByMonth(
          pastConversations.data.results
        )
        const currentMonth = new Date().toLocaleString(this.userLanguage, {
          month: 'long',
        })

        if (organizedConversations[currentMonth]) {
          organizedConversations[currentMonth].active = true
        }
        this.pastConversations = organizedConversations
        this.isLoading = false
      } catch (error) {
        this.showError(error)
        this.isLoading = false
      }
    },

    handleInput(visible) {
      if (!visible) this.$emit('close')
    },

    organizeConversationsByMonth(data) {
      return data.reduce((acc, conversation) => {
        const month = new Date(conversation.timestamp).toLocaleString(
          this.userLanguage,
          {
            month: 'long',
          }
        )

        if (!acc[month])
          acc[month] = { month: month, conversations: [], active: false }
        acc[month].conversations.push({
          ...conversation,
          dateText: new Date(conversation.timestamp).toLocaleString(
            this.userLanguage,
            {
              year: 'numeric',
              day: '2-digit',
              weekday: 'long',
            }
          ),
        })
        return acc
      }, {})
    },

    onPageChange(page) {
      this.currentPage = page
      this.getPastConversations()
    },
  },
}
</script>
<style lang="scss">
.past-conversation-active-class {
  margin-bottom: 5px !important;

  span {
    color: $color-blue-grey-darken3 !important;
    font-weight: bold !important;
  }
}

.selected-conversation {
  background-color: $color-blue-lighten2 !important;
}

.conversation-title {
  text-overflow: ellipsis;
}
.conversation-delete-icon {
  color: $color-blue-grey-lighten1 !important;
  &:hover {
    color: $color-primary !important;
  }
}

.empty-state-container {
  height: 80% !important;
}
</style>
